export const admin = {
  title: "Platform admin",
  badge: "Staff only",
  subtitle: "Run Malfak: people, models, flows and switches. Every change is audited.",
  nav: {
    label: "Admin sections",
    overview: "Overview",
    users: "Users",
    orgs: "Organizations",
    ai: "AI models",
    flows: "Flows & queues",
    audit: "Audit log",
    settings: "Settings & switches",
    plans: "Plans & limits",
  },
  status: {
    ok: "All systems normal",
    degraded: "Degraded",
    down: "Outage",
    checking: "Checking…",
    live: "Live",
    paused: "Paused",
    switchesOn_one: "Kill switches on: {{count}}",
    switchesOn_other: "Kill switches on: {{count}}",
  },
  common: {
    copy: "Copy {{what}}",
    copied: "Copied",
    workflowId: "Workflow id",
    requestId: "Request id",
    openTemporal: "Open in Temporal",
    openLangfuse: "Open trace in Langfuse",
    none: "—",
    reason: "Reason",
    reasonPlaceholder: "Why are you doing this? (kept in the audit log)",
    reasonRequired: "Write at least 3 characters.",
    review: "Review",
    payload: "Payload",
    before: "Before",
    after: "After",
    loadMore: "Load more",
    all: "All",
    from: "From",
    to: "To",
    filters: "Filters",
    clearFilters: "Clear filters",
    noResults: "Nothing matches these filters.",
    changedBy: "Changed by {{who}} · {{when}}",
    neverChanged: "Never changed",
    degraded: "Partly unavailable",
    auditNote: "This action is recorded in the audit log with your name.",
    typeToConfirm: "Type {{word}} to confirm",
    you: "you",
  },
  overview: {
    title: "Overview",
    description: "Health of every dependency, live queues and today's activity.",
    health: "Dependencies",
    healthHint: "Probed by the API every few seconds. Required services make the platform unready when they fail.",
    required: "Required",
    optional: "Optional",
    latency: "{{ms}} ms",
    queues: "Live queues",
    queuesHint: "Backlog per Temporal task queue, webhook inbox and operations.",
    backlog: "Backlog",
    pollers: "Pollers",
    running: "Running",
    unknown: "n/a",
    webhookInbox: "Webhook inbox",
    unprocessed: "Unprocessed",
    failed24h: "Failed (24h)",
    oldest: "Oldest waiting",
    operations: "Operations",
    queued: "Queued",
    approvalsPending: "Approvals waiting",
    activity: "Last 24 hours",
    activityHint: "Webhooks received, automation runs and failures per hour.",
    series: { webhooks: "Webhooks", runs: "Runs", failures: "Failures" },
    versions: "Versions",
    versionApi: "API",
    versionNode: "Node",
    versionEnv: "Environment",
    versionSchema: "Schema",
    tools: "Tools",
    toolHint: { temporalUi: "Workflows, history, task queues", langfuse: "LLM traces and prompts", litellm: "Model gateway admin" },
    toolName: { temporalUi: "Temporal UI", langfuse: "Langfuse", litellm: "LiteLLM" },
    notConfigured: "Not configured",
    switches: "Kill switches",
    allSwitchesOff: "Everything is running normally.",
    manageSwitches: "Manage switches",
    semantic: {
      title: "Semantic index",
      hint: "Dify Knowledge: what smart search and the Copilot can find. Counts come from each organization's last check.",
      dep: "Semantic index",
      state: { ok: "Semantic index: healthy", degraded: "Semantic index: degraded", unavailable: "Semantic index: unavailable" },
      notConfigured: "Not configured: smart search uses the keyword fallback.",
      unreachable: "Dify Knowledge is not reachable.",
      indexed: "Indexed",
      indexing: "Indexing",
      failed: "Failed",
      lastError: "Last error",
      orgsWithErrors: "Organizations with failed documents",
      reindexAll: "Re-index all",
      reindex: "Re-index",
      started: "Re-index started: failed documents are submitted again.",
      checkedAt: "Checked {{when}}",
    },
    kpi: {
      users: "Users",
      orgs: "Organizations",
      accounts: "Channel accounts",
      webhooks24h: "Webhooks (24h)",
      runs24h: "Automation runs (24h)",
      failedRuns24h: "Failed runs (24h)",
      externalActions24h: "Channel actions (24h)",
      disabled: "{{n}} disabled",
      reauth: "{{n}} need re-auth",
      failureRate: "{{pct}} failure rate",
    },
    checkedAt: "Checked {{when}}",
    livePaused: "Live updates pause while this tab is hidden.",
  },
  users: {
    title: "Users",
    description: "Everyone with a Malfak login, across all organizations.",
    search: "Search name or email",
    create: "Create user",
    filterStatus: "Status",
    filterRole: "Platform role",
    statusAll: "Any status",
    roleAll: "Anyone",
    roleAdmins: "Platform admins",
    active: "Active",
    disabled: "Disabled",
    platformAdmin: "Platform admin",
    envAdmin: "Set by PLATFORM_ADMIN_EMAILS: can't be changed here",
    col: { user: "User", orgs: "Organizations", status: "Status", lastLogin: "Last sign-in", sessions: "Sessions", created: "Created", actions: "Actions" },
    neverSignedIn: "Never",
    noOrgs: "No organization",
    actions: {
      menu: "Actions for {{name}}",
      disable: "Disable",
      enable: "Enable",
      resetPassword: "Reset password",
      grantAdmin: "Make platform admin",
      revokeAdmin: "Remove platform admin",
    },
    confirm: {
      disableTitle: "Disable {{name}}?",
      disableBody: "They are signed out everywhere and can't sign in until enabled again. Their data stays.",
      enableTitle: "Enable {{name}}?",
      enableBody: "They can sign in again with their password.",
      resetTitle: "Reset {{name}}'s password?",
      resetBody: "Their sessions end and a one-time temporary password is created. You'll see it once.",
      grantTitle: "Make {{name}} a platform admin?",
      grantBody: "They will see this console and can change anything on the platform.",
      revokeTitle: "Remove platform admin from {{name}}?",
      revokeBody: "They lose access to this console right away.",
    },
    created: "User created",
    updated: "User updated",
    tempPassword: {
      title: "Temporary password",
      body: "Share it with {{email}} over a safe channel. It is shown only this once; they should change it after signing in.",
      once: "Shown once. It won't be visible again.",
      copy: "Copy password",
      done: "I've saved it",
      reveal: "Reveal",
      hide: "Hide",
    },
    form: {
      title: "Create a user",
      description: "They get a one-time temporary password.",
      email: "Email",
      name: "Full name",
      locale: "Language",
      org: "Organization",
      orgMode: "Organization",
      orgExisting: "Existing organization",
      orgNew: "New organization",
      orgSearch: "Search organizations",
      orgPick: "Choose an organization",
      orgNoMatch: "No organization matches",
      orgRequired: "Choose an organization: every user belongs to one.",
      newOrgName: "Organization name",
      newOrgNameRequired: "Enter the organization's name.",
      newOrgOwner: "The new user will be its owner.",
      plan: "Plan",
      role: "Role in that organization",
      platformAdmin: "Platform admin",
      platformAdminHint: "Full access to this console. Use sparingly.",
      emailInvalid: "Enter a valid email.",
      nameRequired: "Enter a name.",
    },
    empty: "No users match.",
  },
  orgs: {
    title: "Organizations",
    description: "Workspaces, plans, members and per-organization switches.",
    search: "Search name or slug",
    planAll: "Any plan",
    col: { org: "Organization", plan: "Plan", members: "Members", accounts: "Accounts", owner: "Owner", created: "Created", overrides: "Overrides" },
    plans: { trial: "Trial", free: "Free", pro: "Pro", business: "Business" },
    create: {
      button: "Create organization",
      title: "Create an organization",
      description: "Name, plan and owner. Audited.",
      name: "Name",
      nameRequired: "Enter a name.",
      plan: "Plan",
      owner: "Owner",
      ownerExisting: "Existing user",
      ownerNew: "New user",
      ownerSearch: "Search users",
      ownerPick: "Choose the owner",
      ownerNoMatch: "No user matches",
      ownerRequired: "Choose the owner.",
      ownerEmail: "Owner email",
      ownerName: "Owner name",
      ownerLocale: "Owner language",
      reviewTitle: "Create {{name}}?",
      reviewBody: "Plan: {{plan}} · Owner: {{owner}}",
      confirm: "Create",
      created: "Organization created",
    },
    roles: { owner: "Owner", admin: "Admin", editor: "Editor", viewer: "Viewer" },
    noOwner: "No owner",
    select: "Select {{name}}",
    selected_one: "Organizations selected: {{count}}",
    selected_other: "Organizations selected: {{count}}",
    clear: "Clear selection",
    applyPlan: "Apply a plan",
    applyTitle: "Apply a plan to the selected organizations",
    applyBody: "Their features and limits change right away (overrides stay).",
    applyDone: "Plan applied to {{updated}} organizations",
    planLink: "Plan & entitlements",
    usage: "Usage",
    open: "Open {{name}}",
    detail: {
      members: "Members",
      accounts: "Channel accounts",
      usage: "Usage (30 days)",
      comments30d: "Comments",
      runs30d: "Automation runs",
      failedRuns30d: "Failed runs",
      plan: "Plan",
      name: "Name",
      saveDetails: "Save details",
      addMember: "Add member",
      addMemberHint: "Pick a user and a role. An organization always keeps at least one owner.",
      pickUser: "Choose a user",
      remove: "Remove {{name}}",
      removeTitle: "Remove {{name}} from {{org}}?",
      removeBody: "They lose access to this organization right away.",
      roleFor: "Role for {{name}}",
      switches: "Switches for this organization",
      switchesHint: "Overrides add to the global switches: if either is on, it's on.",
      inherit: "Use global",
      on: "On",
      off: "Off",
      noAccounts: "No connected accounts.",
      aiOn: "AI on",
      aiOff: "AI off",
      saved: "Organization updated",
    },
    empty: "No organizations match.",
  },
  ai: {
    title: "AI models",
    description: "The app only calls aliases (malfak-*). Point an alias at a different model at runtime, no redeploy.",
    gateway: "LiteLLM gateway",
    reachable: "Reachable",
    unreachable: "Unreachable",
    manageable: "Runtime edits enabled",
    readOnly: "Read-only",
    readOnlyHint: "Model changes need LiteLLM with STORE_MODEL_IN_DB=True and a database. Aliases are still shown.",
    masterKey: "Master key",
    keyMissing: "Not configured",
    keyNote: "The key stays on the server; only a hint is shown.",
    aliases: "Aliases",
    aliasesHint: "What each alias is calling right now.",
    deployments_one: "Deployments: {{count}}",
    deployments_other: "Deployments: {{count}}",
    fallbacks: "Fallbacks",
    noFallbacks: "No fallbacks",
    switch: "Switch model",
    switchTitle: "Switch {{alias}}",
    switchDescription: "Every feature that uses {{alias}} starts using the new model within seconds.",
    switchTo: "New provider model",
    switchToHint: "LiteLLM model id, e.g. openai/gpt-5-mini",
    pickKnown: "Or pick one already in use",
    beforeAfter: "Change",
    switched: "{{alias}} now uses {{model}}",
    warnings: "Heads-up",
    models: "Deployments",
    modelsHint: "Every deployment behind every alias. Config deployments are read-only.",
    col: { alias: "Alias", model: "Provider model", provider: "Provider", mode: "Mode", tags: "Tags", source: "Source", cost: "Cost / 1M tokens", actions: "Actions" },
    source: { config: "config.yaml", db: "Runtime" },
    costIo: "in {{in}} · out {{out}}",
    add: "Add deployment",
    addTitle: "Add a deployment",
    addDescription: "Adds a model behind an alias. Credentials are referenced by environment variable name: raw keys are never accepted.",
    fields: {
      alias: "Alias",
      model: "Provider model",
      credentialRef: "Credential reference",
      credentialRefHint: "e.g. os.environ/LLM_API_KEY (already set on the gateway)",
      apiBaseRef: "API base reference",
      apiBaseRefHint: "e.g. os.environ/LLM_API_BASE",
      mode: "Mode",
      tags: "Tags",
      tagsHint: "Comma separated, e.g. tier:cheap, lang:ar",
    },
    invalidRef: "Use the form os.environ/NAME",
    invalidAlias: "Lowercase letters, digits, . _ - (2–63 chars)",
    invalidTag: "Tags use lowercase letters, digits and : _ . -",
    editTags: "Edit tags for {{model}}",
    tagsSaved: "Tags saved",
    delete: "Remove deployment",
    deleteTitle: "Remove {{model}} from {{alias}}?",
    deleteBody: "Requests to {{alias}} stop going to this deployment. If it's the last one, the alias stops working.",
    deleted: "Deployment removed",
    added: "Deployment added",
    readOnlyRow: "Defined in config.yaml",
    spend: "Spend",
    spendHint: "From the gateway's spend logs.",
    spendDays: "Last {{n}} days",
    spendTotal: "Total",
    spendByModel: "By model",
    spendUnavailable: "Spend isn't available",
    requests: "{{n}} requests",
    modes: { chat: "Chat", embedding: "Embedding", audio_transcription: "Speech to text", audio_speech: "Text to speech", image_generation: "Image" },
  },
  flows: {
    title: "Flows & queues",
    description: "Follow any event end to end: webhook → workflow → rule → action, with links to traces.",
    tabs: { webhooks: "Webhook inbox", workflows: "Workflows", runs: "Automation runs", errors: "Errors" },
    webhooks: {
      status: { received: "Received", processed: "Processed", failed: "Failed" },
      col: { event: "Event", status: "Status", source: "Source", received: "Received", workflow: "Workflow" },
      filterStatus: "Status",
      filterObject: "Object",
      filterSource: "Source",
      search: "Search dedup key or error",
      replays_one: "Replays: {{count}}",
      replays_other: "Replays: {{count}}",
      signedBy: "Signed by",
      processedAt: "Processed",
      error: "Error",
      runs: "Automation runs from this event",
      noRuns: "No automation ran for this event.",
      history: "Replay history",
      noHistory: "Never replayed.",
      replay: "Replay",
      replayTitle: "Replay this event?",
      replayBody: "HandleInboundEvent runs again for the stored payload. Channel actions are idempotent, so nothing is sent twice.",
      replayed: "Replay started: {{id}}",
      replayNotStarted: "Stored, but Temporal didn't start it: {{error}}",
      empty: "No webhook events match.",
    },
    workflows: {
      unavailable: "Temporal isn't reachable",
      unavailableHint: "The list will come back when the API can reach Temporal. Operations stay queued meanwhile.",
      status: {
        RUNNING: "Running",
        COMPLETED: "Completed",
        FAILED: "Failed",
        CANCELLED: "Cancelled",
        TERMINATED: "Terminated",
        CONTINUED_AS_NEW: "Continued",
        TIMED_OUT: "Timed out",
        UNKNOWN: "Unknown",
      },
      col: { workflow: "Workflow", type: "Type", queue: "Task queue", status: "Status", started: "Started", duration: "Duration" },
      filterQueue: "Task queue",
      filterStatus: "Status",
      filterType: "Type",
      search: "Workflow id starts with…",
      history: "History",
      historyTruncated: "Showing the first events only.",
      pending: "Pending activities",
      attempt: "attempt {{n}}",
      input: "Input",
      result: "Result",
      failure: "Failure",
      memo: "Memo",
      events_one: "Events: {{count}}",
      events_other: "Events: {{count}}",
      cancel: "Cancel",
      terminate: "Terminate",
      retry: "Retry",
      cancelTitle: "Cancel {{id}}?",
      cancelBody: "Asks the workflow to stop; it can clean up first.",
      terminateTitle: "Terminate {{id}}?",
      terminateBody: "Stops it immediately, with no cleanup. Use only when cancel doesn't work.",
      retryTitle: "Retry {{id}}?",
      retryBody: "Resets it to the first workflow task: it runs again with the same input and id.",
      done: { cancel: "Cancellation requested", terminate: "Workflow terminated", retry: "Workflow reset: run {{runId}}" },
      failed: "Temporal refused: {{error}}",
      empty: "No workflows match.",
      next: "Next page",
      first: "First page",
    },
    runs: {
      col: { when: "When", org: "Organization", rule: "Rule", event: "Event", result: "Result", reason: "Reason" },
      filterOrg: "Organization",
      filterResult: "Result",
      result: { executed: "Executed", pending_approval: "Waiting approval", skipped: "Skipped", failed: "Failed", suggested: "Suggested" },
      noRule: "No rule matched",
      empty: "No runs match.",
    },
    errors: {
      kind: { webhook: "Webhook", operation: "Operation", external_action: "Channel action", automation_run: "Automation run" },
      empty: "No recent failures. 🎉",
      hint: "The latest persisted failures across the platform.",
    },
  },
  audit: {
    title: "Audit log",
    description: "Who did what, when, with before and after. Entries can't be edited.",
    search: "Search target or action",
    actor: "Actor id or email",
    org: "Organization",
    action: "Action starts with",
    col: { when: "When", actor: "Actor", action: "Action", target: "Target", org: "Organization" },
    system: "System",
    details: "Show details",
    hideDetails: "Hide details",
    changes: "Changes",
    noChanges: "No before/after recorded.",
    empty: "No audit entries match.",
    platform: "Platform",
  },
  settings: {
    title: "Settings & kill switches",
    description: "Platform-wide switches. They take effect within seconds in the API and the worker.",
    danger: "Danger zone",
    dangerHint: "These stop real work for every customer. Every change is audited.",
    on: "On",
    off: "Off",
    turnOn: "Turn on",
    turnOff: "Turn off",
    switches: {
      automationPaused: {
        title: "Pause all automation",
        body: "Rules stop running. Incoming comments and DMs are still stored; each run is recorded as skipped with the reason.",
        onLabel: "Automation is paused",
      },
      aiDisabled: {
        title: "Turn AI off",
        body: "No LLM calls. AI features answer “unavailable”; automation falls back to keyword rules and sends AI-dependent actions to human approval.",
        onLabel: "AI is off",
      },
      outboundDryRun: {
        title: "Outbound dry-run",
        body: "Nothing is sent to channels: replies, DMs, hides and publishing are recorded but not executed.",
        onLabel: "Dry-run: nothing is sent",
      },
    },
    confirmOnTitle: "Turn on “{{name}}” for everyone?",
    confirmOffTitle: "Turn off “{{name}}”?",
    confirmOffBody: "Normal operation resumes for every organization without an override.",
    changed: "Switch updated",
    banner: "Maintenance banner",
    bannerHint: "Shown at the top of the app for every signed-in user. Leave empty to hide it.",
    bannerPlaceholder: "e.g. Scheduled maintenance tonight 23:00–23:30 GST. Publishing may be delayed.",
    preview: "Preview",
    saveBanner: "Publish banner",
    clearBanner: "Remove banner",
    bannerSaved: "Banner updated",
    bannerCleared: "Banner removed",
    overrides: "Per-organization overrides",
    overridesHint: "Organizations with their own switches. Edit them from the organization page.",
    noOverrides: "No organization has overrides.",
    effective: "In effect",
    col: { org: "Organization", overrides: "Overrides", effective: "In effect" },
  },
  banner: {
    label: "Platform notice",
    dismiss: "Dismiss notice",
    switchPill: "Kill switch on",
  },
  denied: "You don't have access to the admin console.",
  plansAdmin: {
    title: "Plans & limits",
    description: "Features and limits of every plan. Editing a plan applies to every organization on it, unless overridden.",
    version: "v{{version}}",
    orgCount_one: "Organizations: {{count}}",
    orgCount_other: "Organizations: {{count}}",
    features: "Features",
    limits: "Limits",
    feature: "Feature",
    limit: "Limit",
    unlimitedHint: "Empty = unlimited",
    trialDays: "Trial length (days)",
    review: "Review changes",
    reset: "Discard",
    noChanges: "No changes yet",
    changesCount_one: "Changes: {{count}}",
    changesCount_other: "Changes: {{count}}",
    reviewTitle: "Save plan changes",
    reviewBody: "Every organization on these plans gets the new features and limits (unless overridden).",
    saved: "Plans saved",
    invalidLimit: "Enter a number of 0 or more, or leave it empty for unlimited",
    invalidTrial: "1 to 365 days",
    log: "Change log",
    logTitle: "Entitlement change log",
    logDescription: "Every plan and entitlement change: who, when, before → after and the reason.",
    logEmpty: "No changes yet",
    filterOrg: "Organization",
    filterPlan: "Plan",
    any: "Any",
    by: "by {{who}}",
    system: "System",
    actions: {
      plan_updated: "Plan edited",
      org_plan_set: "Plan changed",
      org_status_set: "Status changed",
      org_trial_extended: "Trial extended",
      org_trial_ended: "Trial ended",
      override_created: "Override added",
      override_revoked: "Override revoked",
      plan_bulk_applied: "Plan applied (bulk)",
      ai_key_rotated: "AI key rotated",
      ai_key_provisioned: "AI key created",
      backfill: "Backfill",
    },
    org: {
      back: "All organizations",
      title: "Plan & entitlements",
      assigned: "Assigned plan",
      effective: "Effective plan",
      status: "Status",
      planPanel: "Plan",
      save: "Save",
      saveTitle: "Change the plan of {{org}}",
      saveBody: "Features and limits change right away.",
      saved: "Saved",
      trial: "Trial",
      trialEnds: "Trial ends {{date}}",
      trialEnded: "The trial ended: the organization is on Free",
      noTrial: "Not on a trial",
      extendDays: "Days",
      extend: "Extend trial",
      end: "End trial now",
      extendTitle: "Extend the trial of {{org}} by {{days}} days",
      endTitle: "End the trial of {{org}} now",
      endBody: "The organization falls back to Free immediately.",
      usage: "Usage vs limits",
      history: "History",
      historyOf: "History of {{what}}",
      overrides: "Overrides",
      overridesHint: "Override one feature or limit for this organization (optional expiry, reason required).",
      noOverrides: "No overrides",
      addOverride: "Add override",
      kind: "Type",
      kindFeature: "Feature",
      kindLimit: "Limit",
      key: "Which",
      value: "Value",
      on: "On",
      off: "Off",
      expires: "Expires (optional)",
      never: "Never",
      expiresOn: "until {{date}}",
      revoke: "Revoke",
      revokeTitle: "Revoke the override of {{what}}",
      state: { active: "Active", expired: "Expired", revoked: "Revoked" },
      createTitle: "Add an override for {{org}}",
      errors: { key: "Choose a feature or limit", value: "Enter a number of 0 or more, or leave it empty for unlimited", expiresAt: "The expiry must be in the future" },
      ai: "AI budget & key",
      aiHint: "The organization's LiteLLM virtual key: its monthly budget is the AI budget limit.",
      mode: { virtual_key: "Own virtual key", global_key: "Shared key (spend attribution only)" },
      keyAlias: "Key alias",
      keyHint: "Key",
      rpm: "Requests per minute",
      tpm: "Tokens per minute",
      spend: "Spend this month",
      exceeded: "Budget used up",
      resets: "Resets {{date}}",
      rotate: "Rotate key",
      rotateTitle: "Rotate the AI key of {{org}}",
      rotateBody: "A new key is issued and the old one stops working.",
      rotated: "Key rotated",
      editBudget: "Edit budget",
      budgetTitle: "Set the monthly AI budget of {{org}}",
      budgetBody: "Saved as an override of the AI budget limit (USD per month; empty = unlimited).",
      budgetLabel: "Monthly AI budget (USD)",
      openLog: "Change log of this organization",
    },
  },
};
